import { cn } from '@/lib/utils'
import { useRtlDirection } from '@/lib/hooks/helpers/useRtlDirection'
import React, { useEffect, useRef, useState } from 'react'

type CenteredItemProps = {
  children: React.ReactNode
  height: number
  className?: string
}

export type InputProps = React.ComponentPropsWithoutRef<'input'> & {
  ref?: React.Ref<HTMLInputElement | null>
  error?: string | boolean
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  height?: number
  watchedValue?: string
  label?: string
}

const CenteredItem = ({ children, height, className }: CenteredItemProps) => (
  <div
    style={{ height }}
    className={cn(
      `pointer-events-none absolute top-0 flex w-full items-center px-3`,
      className,
    )}
  >
    {children}
  </div>
)

const Input = ({
  ref,
  className,
  type,
  error,
  iconLeft,
  iconRight,
  height = 50,
  label,
  ...props
}: InputProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const { isRtl } = useRtlDirection()

  const [hasValue, setHasValue] = useState(false)

  useEffect(() => {
    const node = inputRef.current

    const autoFillTimer = setTimeout(() => {
      if (node?.matches(':autofill')) {
        setHasValue(true)
      }
    }, 150)

    return () => clearTimeout(autoFillTimer)
  }, [])

  useEffect(() => {
    const node = inputRef.current

    if (props.value || props.defaultValue || node?.value) setHasValue(true)

    const listen = (data: unknown): void => {
      const target = (data as { target?: HTMLInputElement })?.target
      const value = target?.value

      if (value && value.length > 0) {
        setHasValue(true)
      } else {
        setHasValue(false)
      }
    }

    const handleFocus = (): void => {
      setHasValue(true)
    }

    if (node) {
      node.addEventListener('input', listen)
      node.addEventListener('focus', handleFocus)
      node.addEventListener('blur', listen)

      return () => {
        node.removeEventListener('input', listen)
        node.removeEventListener('focus', handleFocus)
        node.addEventListener('blur', listen)
      }
    }

    return () => {}
  }, [props.defaultValue, props.value, inputRef])

  useEffect(() => {
    if (props.watchedValue === undefined) return

    if (props.watchedValue !== '') {
      setHasValue(true)
    } else if (props.watchedValue === '') {
      setHasValue(false)
    }
  }, [props.watchedValue])

  return (
    <div className={cn('relative w-full', error && 'mb-3')}>
      <CenteredItem height={height}>
        <label
          htmlFor={props.id}
          className={cn(
            `text-sm transition-all duration-200`,
            hasValue && '-translate-y-[15px] pt-2 text-xs text-slate-500',
            error && 'text-red-500',
            iconLeft ? (isRtl ? 'pr-[25px]' : 'pl-[25px]') : undefined,
          )}
        >
          {label}
        </label>
      </CenteredItem>
      <input
        type={type}
        style={{ height }}
        className={cn(
          `flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 pb-0 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:border-primary focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50`,
          error && 'border-red-500 text-red-500 focus-visible:border-red-500',
          iconLeft ? (isRtl ? 'pr-[35px]' : 'pl-[35px]') : undefined,
          iconRight ? (isRtl ? 'pl-[35px]' : 'pr-[35px]') : undefined,
          !hasValue && label && 'placeholder-transparent',

          className,
        )}
        ref={(node) => {
          inputRef.current = node

          if (typeof ref === 'function') {
            ref(node)
          } else if (ref) {
            // TODO: Mutating object props is generally not a good practice.
            // eslint-disable-next-line no-param-reassign
            ref.current = node
          }
        }}
        {...props}
      />
      {iconLeft && (
        <CenteredItem height={height} className={isRtl ? 'right-0' : 'left-0'}>
          {iconLeft}
        </CenteredItem>
      )}
      {iconRight && (
        <CenteredItem
          height={height}
          className={cn('justify-end', isRtl ? 'left-0' : 'right-0')}
        >
          {iconRight}
        </CenteredItem>
      )}
      <span className="text-sm text-red-400">{error}</span>
    </div>
  )
}

export { Input }
